import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { X, ChevronDown } from "lucide-react";

export function SearchableSelect({ options = [], value, onChange, placeholder = "Search...", displayKey = "label", valueKey = "value", searchKeys, renderOption, disabled = false, className = "", testid = "" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const ref = useRef(null);

  const selected = options.find(o => (typeof o === "string" ? o : o[valueKey]) === value);
  const displayValue = selected ? (typeof selected === "string" ? selected : selected[displayKey]) : "";

  const filtered = query
    ? options.filter(o => {
        const q = query.toLowerCase();
        if (typeof o === "string") return o.toLowerCase().includes(q);
        if (searchKeys && searchKeys.length > 0) {
          return searchKeys.some(k => (o[k] || "").toString().toLowerCase().includes(q));
        }
        return (o[displayKey] || "").toLowerCase().includes(q);
      })
    : options;

  useEffect(() => {
    const handleClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    const val = typeof opt === "string" ? opt : opt[valueKey];
    onChange(val);
    setQuery("");
    setOpen(false);
    setHighlighted(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted(prev => Math.min(prev + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted(prev => Math.max(prev - 1, 0)); }
    else if (e.key === "Enter" && highlighted >= 0 && filtered[highlighted]) { e.preventDefault(); handleSelect(filtered[highlighted]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  if (disabled) {
    return <div className={`h-8 flex items-center px-2 text-xs bg-zinc-50 border border-zinc-200 rounded-sm text-zinc-500 ${className}`}>{displayValue || placeholder}</div>;
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      {value && displayValue ? (
        <div className="flex items-center h-8 border border-zinc-200 rounded-sm bg-white">
          <span className="flex-1 px-2 text-xs truncate">{displayValue}</span>
          <button onClick={() => { onChange(""); setQuery(""); }} className="px-1.5 hover:bg-zinc-100 h-full flex items-center"><X size={12} className="text-zinc-400" /></button>
        </div>
      ) : (
        <div className="relative">
          <Input
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); setHighlighted(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-8 text-xs rounded-sm pr-7"
            data-testid={testid || undefined}
          />
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        </div>
      )}
      {open && (
        <div className="absolute z-[9999] mt-1 w-full bg-white border border-zinc-200 rounded-sm shadow-lg max-h-[240px] overflow-y-auto" data-testid="searchable-select-dropdown" style={{position: 'absolute', top: '100%'}}>
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-zinc-400">No results found</p>
          ) : filtered.map((opt, i) => {
            const label = typeof opt === "string" ? opt : opt[displayKey];
            const val = typeof opt === "string" ? opt : opt[valueKey];
            return (
              <button
                key={val}
                onClick={() => handleSelect(opt)}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 border-b border-zinc-50 ${highlighted === i ? 'bg-blue-50' : ''} ${val === value ? 'font-semibold text-blue-700' : ''}`}
              >
                {renderOption ? renderOption(opt) : label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
